import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, EmptyBox } from "./shared";
import { formatApiError } from "@/lib/api";
import { Bot, Plus, Trash2, Copy, ExternalLink, Save, MessagesSquare, DollarSign, Users, Mail,
  Globe, Loader2, BarChart3, Tag, Zap } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const blank = { name: "", client_name: "", brand_color: "#C9A227", greeting: "", knowledge: "", website_url: "", notify_email: "", plan: "starter", active: true };

export function AiBots() {
  const [bots, setBots] = useState(null);
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importDeep, setImportDeep] = useState(false);
  const [leadsFor, setLeadsFor] = useState(null);
  const [leads, setLeads] = useState(null);
  const [analyticsFor, setAnalyticsFor] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [plansEditor, setPlansEditor] = useState(null); // array while editing

  const load = () => api.get("/admin/bots").then((r) => { setBots(r.data.bots); setPlans(r.data.plans || []); }).catch(() => setBots([]));
  useEffect(() => { load(); }, []);

  const planName = (id) => (plans.find((p) => p.id === id) || {}).name || id;

  const viewLeads = async (bot) => {
    setLeadsFor(bot); setLeads(null);
    try { const r = await api.get(`/admin/bots/${bot.id}/leads`); setLeads(r.data.leads); }
    catch (e) { toast.error(formatApiError(e)); setLeads([]); }
  };
  const viewAnalytics = async (bot) => {
    setAnalyticsFor(bot); setAnalytics(null);
    try { const r = await api.get(`/admin/bots/${bot.id}/analytics`); setAnalytics(r.data.series); }
    catch (e) { toast.error(formatApiError(e)); setAnalytics([]); }
  };

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Give your bot a name."); return; }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/bots/${editing.id}`, editing);
      else await api.post("/admin/bots", editing);
      toast.success("Saved."); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this bot? Its transcripts and leads will be removed too.")) return;
    try { await api.delete(`/admin/bots/${id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doImport = async () => {
    if (!importUrl.trim()) { toast.error("Paste a page URL first."); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/admin/bots/import-url", { url: importUrl.trim(), deep: importDeep });
      setEditing((e) => ({ ...e, knowledge: (e.knowledge ? e.knowledge + "\n\n" : "") + data.text }));
      setImportUrl("");
      toast.success(`Imported ${data.chars.toLocaleString()} characters from ${data.pages} page${data.pages !== 1 ? "s" : ""}.`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };
  const sendDigest = async () => {
    try { const r = await api.post("/admin/bots/send-digest-now"); toast.success(`Roundup sent — ${r.data.total_leads} lead${r.data.total_leads !== 1 ? "s" : ""} this week.`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const resendWelcome = async (id) => {
    try { await api.post(`/admin/bots/${id}/resend-welcome`); toast.success("Welcome email sent to the client."); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const savePlans = async () => {
    try { const r = await api.put("/admin/bot-plans", { plans: plansEditor }); setPlans(r.data.plans); setPlansEditor(null); toast.success("Plans saved."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied to clipboard."); };

  if (bots === null) return <Loader />;

  // ---------- Plans editor ----------
  if (plansEditor) {
    const setP = (i, k, v) => setPlansEditor(plansEditor.map((p, j) => j === i ? { ...p, [k]: v } : p));
    return (
      <div data-testid="bot-plans-editor">
        <button onClick={() => setPlansEditor(null)} className="text-xs text-gray-400 hover:text-white mb-4">← Back to bots</button>
        <Head title="Bot Plans" desc="Ready-to-sell monthly plans you can hand clients. Each has a message allowance; bots keep answering over the limit so you can bill overage." />
        <div className="space-y-3 max-w-3xl">
          {plansEditor.map((p, i) => (
            <div key={i} className="bg-navy-800 border border-white/10 p-4 grid sm:grid-cols-4 gap-3 items-end" data-testid={`plan-row-${i}`}>
              <Field label="Plan name"><input value={p.name} onChange={(e) => setP(i, "name", e.target.value)} className={inp} data-testid={`plan-name-${i}`} /></Field>
              <Field label="Price ($/mo)"><input type="number" value={p.price_usd} onChange={(e) => setP(i, "price_usd", e.target.value)} className={inp} data-testid={`plan-price-${i}`} /></Field>
              <Field label="Messages / month"><input type="number" value={p.monthly_messages} onChange={(e) => setP(i, "monthly_messages", e.target.value)} className={inp} data-testid={`plan-msgs-${i}`} /></Field>
              <button onClick={() => setPlansEditor(plansEditor.filter((_, j) => j !== i))} className="flex items-center gap-1 text-xs text-red-300 hover:text-red-200 pb-3"><Trash2 size={13} /> Remove</button>
            </div>
          ))}
          <button onClick={() => setPlansEditor([...plansEditor, { id: "", name: "New Plan", price_usd: 0, monthly_messages: 500 }])}
            data-testid="plan-add" className="flex items-center gap-2 text-sm text-gray-300 hover:text-white border border-white/10 px-3 py-2"><Plus size={14} /> Add plan</button>
          <div className="pt-2">
            <button onClick={savePlans} data-testid="plans-save" className="flex items-center gap-2 bg-gold text-navy px-5 py-2.5 text-sm font-semibold hover:bg-gold-light"><Save size={15} /> Save Plans</button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Leads ----------
  if (leadsFor) {
    return (
      <div data-testid="ai-bots-leads">
        <button onClick={() => { setLeadsFor(null); setLeads(null); }} className="text-xs text-gray-400 hover:text-white mb-4">← Back to bots</button>
        <Head title={`Leads — ${leadsFor.name}`} desc="People who left their details in this bot. Forward these to the client." />
        {leads === null ? <Loader /> : leads.length === 0 ? <EmptyBox text="No leads captured yet." /> : (
          <div className="space-y-3 max-w-2xl">
            {leads.map((l) => (
              <div key={l.id} className="bg-navy-800 border border-white/10 p-4" data-testid={`bot-lead-${l.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-white font-semibold">{l.name}</div>
                  <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 text-sm text-gold hover:underline"><Mail size={13} /> {l.email}</a>
                </div>
                {l.message && <p className="text-sm text-gray-300 mt-2 whitespace-pre-wrap">{l.message}</p>}
                <div className="text-[11px] text-gray-500 mt-2">{new Date(l.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- Analytics ----------
  if (analyticsFor) {
    return (
      <div data-testid="ai-bots-analytics">
        <button onClick={() => { setAnalyticsFor(null); setAnalytics(null); }} className="text-xs text-gray-400 hover:text-white mb-4">← Back to bots</button>
        <Head title={`Analytics — ${analyticsFor.name}`} desc="Messages and leads over the last 30 days. See which bots are earning their keep." />
        {analytics === null ? <Loader /> : (
          <div className="bg-navy-800 border border-white/10 p-4" style={{ height: 380 }} data-testid="bot-analytics-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics} margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fill: "#7a839a", fontSize: 11 }} interval={4} />
                <YAxis tick={{ fill: "#7a839a", fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0b1220", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="messages" name="Messages" fill="#C9A227" radius={[2, 2, 0, 0]} />
                <Bar dataKey="leads" name="Leads" fill="#4ade80" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
  }

  // ---------- Editor ----------
  if (editing) {
    return (
      <div data-testid="ai-bots-editor">
        <Head title={editing.id ? "Edit AI Bot" : "New AI Bot"} desc="Brand it, feed it the client's info, then share or embed it." />
        <div className="max-w-2xl space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Bot name"><input data-testid="bot-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inp} placeholder="e.g. Bella's Bakery Assistant" /></Field>
            <Field label="Client / business name"><input data-testid="bot-client" value={editing.client_name} onChange={(e) => setEditing({ ...editing, client_name: e.target.value })} className={inp} placeholder="Bella's Bakery" /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Brand color">
              <div className="flex items-center gap-2">
                <input type="color" value={editing.brand_color} onChange={(e) => setEditing({ ...editing, brand_color: e.target.value })} className="w-10 h-10 bg-transparent border border-white/15 cursor-pointer" data-testid="bot-color" />
                <input value={editing.brand_color} onChange={(e) => setEditing({ ...editing, brand_color: e.target.value })} className={inp} />
              </div>
            </Field>
            <Field label="Website (optional)"><input value={editing.website_url} onChange={(e) => setEditing({ ...editing, website_url: e.target.value })} className={inp} placeholder="https://…" /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Plan">
              <select value={editing.plan || "starter"} onChange={(e) => setEditing({ ...editing, plan: e.target.value })} className={inp} data-testid="bot-plan">
                {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — ${p.price_usd}/mo · {p.monthly_messages.toLocaleString()} msgs</option>)}
              </select>
            </Field>
            <Field label="Lead alert email (per bot)"><input data-testid="bot-notify-email" value={editing.notify_email} onChange={(e) => setEditing({ ...editing, notify_email: e.target.value })} className={inp} placeholder="client@business.com" /></Field>
          </div>
          <Field label="Client portal login email (so they can see leads & usage)"><input data-testid="bot-client-email" value={editing.client_user_email || ""} onChange={(e) => setEditing({ ...editing, client_user_email: e.target.value })} className={inp} placeholder="The email your client registers with" /></Field>
          <Field label="Greeting message"><input data-testid="bot-greeting" value={editing.greeting} onChange={(e) => setEditing({ ...editing, greeting: e.target.value })} className={inp} placeholder="Hi! How can I help you today?" /></Field>

          <div className="bg-navy-800 border border-white/10 p-3">
            <div className="text-xs text-gray-400 mb-1.5 flex items-center gap-1.5"><Globe size={13} className="text-gold" /> Import from a webpage</div>
            <div className="flex items-center gap-2">
              <input value={importUrl} onChange={(e) => setImportUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doImport()}
                placeholder="Paste a page URL (e.g. the client's About or Services page)" className={inp} data-testid="bot-import-url" />
              <button onClick={doImport} disabled={importing} data-testid="bot-import-btn"
                className="flex items-center gap-1.5 bg-white/10 text-white px-4 py-2.5 text-sm font-semibold hover:bg-white/15 disabled:opacity-40 whitespace-nowrap">
                {importing ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Import
              </button>
            </div>
            <p className="text-[11px] text-gray-500 mt-1">Pulls that page's text into the knowledge base below. Review it before saving.</p>
            <label className="flex items-center gap-2 text-xs text-gray-300 mt-2">
              <input type="checkbox" checked={importDeep} onChange={(e) => setImportDeep(e.target.checked)} data-testid="bot-import-deep" />
              Also grab key sub-pages (About, Services, Contact…)
            </label>
          </div>

          <Field label="Knowledge base — what should it know?">
            <textarea data-testid="bot-knowledge" value={editing.knowledge} onChange={(e) => setEditing({ ...editing, knowledge: e.target.value })} rows={8} className={inp}
              placeholder="Paste the business info the bot should answer from: services, hours, pricing, location, FAQs, policies…" />
            <p className="text-[11px] text-gray-500 mt-1">The bot answers only from this. Leave gaps and it will offer to pass the question to the team.</p>
          </Field>
          <label className="flex items-center gap-2 text-sm text-gray-300"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="bot-active" /> Active (visitors can use it)</label>
          <div className="flex gap-3 pt-2">
            <button onClick={save} disabled={saving} data-testid="bot-save" className="flex items-center gap-2 bg-gold text-navy px-5 py-2.5 text-sm font-semibold hover:bg-gold-light disabled:opacity-40"><Save size={15} /> Save Bot</button>
            <button onClick={() => setEditing(null)} className="px-4 py-2.5 text-sm text-gray-400 hover:text-white">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- List ----------
  return (
    <div data-testid="ai-bots">
      <Head title="AI Bots" desc="Brandable AI assistants you can resell to clients — share a link or embed on any site. Usage is metered per bot." />
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => setEditing({ ...blank, plan: plans[0]?.id || "starter" })} data-testid="bot-new" className="flex items-center gap-2 bg-gold text-navy px-4 py-2.5 text-sm font-semibold hover:bg-gold-light"><Plus size={15} /> New Bot</button>
        <button onClick={() => setPlansEditor(plans.map((p) => ({ ...p })))} data-testid="bot-plans-btn" className="flex items-center gap-2 border border-white/15 text-gray-200 px-4 py-2.5 text-sm hover:bg-white/5"><Tag size={15} /> Manage Plans</button>
        {bots.length > 0 && <button onClick={sendDigest} data-testid="bot-digest-btn" className="flex items-center gap-2 border border-white/15 text-gray-200 px-4 py-2.5 text-sm hover:bg-white/5"><Mail size={15} /> Email weekly roundup</button>}
      </div>

      {bots.length === 0 ? <EmptyBox text="No bots yet. Create your first client bot." /> : (
        <div className="space-y-4">
          {bots.map((b) => {
            const link = `${BACKEND}/bot/${b.public_key}`;
            const snippet = `<script src="${BACKEND}/embed.js" data-bot="${b.public_key}" data-color="${b.brand_color}"></script>`;
            const u = b.usage || {};
            const pct = u.allowance ? Math.min(100, Math.round((u.messages / u.allowance) * 100)) : null;
            return (
              <div key={b.id} className="bg-navy-800 border border-white/10 p-5" data-testid={`bot-card-${b.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: b.brand_color, color: "#0b1220" }}><Bot size={18} /></span>
                    <div>
                      <div className="text-white font-semibold flex items-center gap-2">{b.name}
                        <span className="text-[10px] uppercase tracking-wide text-gold border border-gold/30 px-1.5 py-0.5">{planName(b.plan || "starter")}</span>
                        {!b.active && <span className="text-[10px] uppercase tracking-wide text-amber-300 border border-amber-400/30 px-1.5 py-0.5">inactive</span>}
                        {u.over_allowance && <span className="text-[10px] uppercase tracking-wide text-red-300 border border-red-400/30 px-1.5 py-0.5">over allowance</span>}
                        {u.near_limit && <span className="text-[10px] uppercase tracking-wide text-amber-300 border border-amber-400/30 px-1.5 py-0.5">nearing limit</span>}
                      </div>
                      <div className="text-xs text-gray-400">{b.client_name || "—"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1.5 text-gray-300" title="Conversations this month"><MessagesSquare size={14} className="text-gold" /> {u.conversations ?? 0}</span>
                    <span className="flex items-center gap-1.5 text-gray-300" title="Est. cost this month"><DollarSign size={14} className="text-gold" /> ${(u.est_cost_usd ?? 0).toFixed(2)}</span>
                    <button onClick={() => viewLeads(b)} data-testid={`bot-leads-${b.id}`} className="flex items-center gap-1.5 text-gray-300 hover:text-white" title="Leads captured"><Users size={14} className="text-gold" /> {u.leads ?? 0}</button>
                    <button onClick={() => viewAnalytics(b)} data-testid={`bot-analytics-${b.id}`} className="text-gray-300 hover:text-white" title="Analytics"><BarChart3 size={16} className="text-gold" /></button>
                    {b.client_user_email && <button onClick={() => resendWelcome(b.id)} data-testid={`bot-resend-${b.id}`} className="text-gray-300 hover:text-white" title="Resend welcome email"><Mail size={16} className="text-gold" /></button>}
                    <button onClick={() => setEditing(b)} data-testid={`bot-edit-${b.id}`} className="text-xs text-gray-300 hover:text-white border border-white/10 px-3 py-1.5">Edit</button>
                    <button onClick={() => remove(b.id)} data-testid={`bot-delete-${b.id}`} className="flex items-center gap-1 text-xs text-red-300 hover:text-red-200"><Trash2 size={13} /> Delete</button>
                  </div>
                </div>

                {pct !== null && (
                  <div className="mt-4" data-testid={`bot-allowance-${b.id}`}>
                    <div className="flex justify-between text-[11px] text-gray-500 mb-1">
                      <span>Monthly messages</span>
                      <span>{(u.messages ?? 0).toLocaleString()} / {u.allowance.toLocaleString()}</span>
                    </div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: u.over_allowance ? "#f87171" : u.near_limit ? "#fbbf24" : "#C9A227" }} />
                    </div>
                    {(u.near_limit || u.over_allowance) && (
                      <p className={`text-[11px] mt-1.5 ${u.over_allowance ? "text-red-300" : "text-amber-300"}`}>
                        {u.over_allowance ? "Over plan allowance — great moment to move this client up a tier." : "Nearing the plan limit — consider suggesting an upgrade."}
                      </p>
                    )}
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-3 mt-4">
                  <div className="bg-navy border border-white/10 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">Shareable link</div>
                    <div className="flex items-center gap-2">
                      <code className="text-xs text-gold truncate flex-1">{link}</code>
                      <button onClick={() => copy(link)} className="text-gray-400 hover:text-white" title="Copy"><Copy size={14} /></button>
                      <a href={link} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white" title="Open"><ExternalLink size={14} /></a>
                    </div>
                  </div>
                  <div className="bg-navy border border-white/10 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">Embed on any website</div>
                    <div className="flex items-center gap-2">
                      <code className="text-xs text-gray-300 truncate flex-1">{snippet}</code>
                      <button onClick={() => copy(snippet)} data-testid={`bot-copy-embed-${b.id}`} className="text-gray-400 hover:text-white" title="Copy"><Copy size={14} /></button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const inp = "w-full bg-navy border border-white/15 text-sm text-gray-200 px-3 py-2.5 focus:border-gold/50 outline-none";
const Field = ({ label, children }) => (<div><div className="text-xs text-gray-400 mb-1.5">{label}</div>{children}</div>);
